import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useUser } from '../stores/appStore';

// Layout components (keep as eager imports for better UX)
// リデザイン v2: 認証後アプリの骨格は AppLayout (AppShell ベース)。旧 DashboardLayout は撤廃。
import { AppLayout } from '../layouts/AppLayout';
// ReonboardingGate (Task C4 - OnboardingGuard の拡張版)
import { ReonboardingGate } from '../components/onboarding/ReonboardingGate';
// 会議インポート機能フラグ（VITE_MEETING_IMPORT_ENABLED）
import { isMeetingImportEnabled } from '../utils/featureFlags';

// Landing page (lazy load) — リデザイン v2 (自前ナビ/フッター、LandingLayout 不要)
const LandingPage = lazy(() => import('../pages/LandingPage'));
const DemoPage = lazy(() => import('../pages/DemoPage'));
// サービス説明ページ（認証不要・公開）
const UseCasesPage = lazy(() => import('../pages/UseCasesPage'))
// 料金ページ（認証不要・公開）
const PricingPage = lazy(() => import('../pages/PricingPage'));

// Auth pages — リデザイン v2 (自前 AuthShell、AuthLayout 不要)
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';

// OAuth Callback page (lazy load)
const AuthCallbackPage = lazy(() => import('../pages/auth/AuthCallbackPage'));

// Dashboard pages (lazy load for performance)
// リデザイン v2: ホーム「今日」(旧 DashboardPage を置換)
const TodayPage = lazy(() => import('../pages/TodayPage'));
// リデザイン v2: プロジェクト一覧・詳細 (旧 projects ページを置換)
const ProjectsPage = lazy(() => import('../pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('../pages/ProjectDetailPage'));
// リデザイン v2: 使用量 (旧 UsagePage を置換)
const UsagePage = lazy(() => import('../pages/UsagePage'));

// Realtime pages (lazy load)
// リデザイン v2: 会議中 (旧 RealtimePage を置換)
const RealtimePage = lazy(() => import('../pages/MeetingLivePage'));
// リデザイン v2: 会議準備 (旧 MeetingSetupPage を置換)
const MeetingSetupPage = lazy(() => import('../pages/MeetingPrepPage'));
// リデザイン v2: 会議履歴 (旧 SessionHistoryPage を置換)
const SessionHistoryPage = lazy(() => import('../pages/SessionHistoryPage'));
// リデザイン v2: 会議のまとめ (旧 SessionDetailPage を置換)
const SessionDetailPage = lazy(() => import('../pages/SessionDetailPage'));

// Contacts & Companies pages (lazy load)
// リデザイン v2: 相手一覧 (旧 ContactsPage を置換)
const ContactsPage = lazy(() => import('../pages/ContactsPage'));
// リデザイン v2: 相手詳細 (記憶活用の核心。旧 ContactDetailPage を置換)
const ContactDetailPage = lazy(() => import('../pages/ContactDetailPage'));
// リデザイン v2: 会社一覧・詳細 (旧 companies ページを置換)
const CompaniesPage = lazy(() => import('../pages/CompaniesPage'));
const CompanyDetailPage = lazy(() => import('../pages/CompanyDetailPage'));

// Portal pages (lazy load)
// リデザイン v2: アカウント設定 (旧 AccountPage を置換)
const AccountPage = lazy(() => import('../pages/AccountPage'));
// リデザイン v2: 課金 (旧 BillingPage を置換)
const BillingPage = lazy(() => import('../pages/BillingPage'));
// リデザイン v2: API キー管理 (旧 ApiKeysPage を置換)
const APIKeysPage = lazy(() => import('../pages/ApiKeysPage'));
const IntegrationsPage = lazy(() => import('../pages/IntegrationsPage'));

// CRM Integration pages (lazy load)
const CRMIntegrationPage = lazy(() => import('../pages/CRMIntegrationPage'));

// Preparation page (lazy load)
const PreparationPage = lazy(() => import('../pages/PreparationPage'));

// Meeting Import page (lazy load, VITE_MEETING_IMPORT_ENABLED でガード)
const ImportMeetingPage = lazy(() => import('../pages/ImportMeetingPage'));

// Workspace pages (lazy load)
const WorkspacesSettingsPage = lazy(() => import('../pages/WorkspacesSettingsPage'));

// Onboarding Settings page (lazy load) - Task C5
const OnboardingSettingsPage = lazy(() => import('../pages/OnboardingSettingsPage'));

// Onboarding pages (lazy load)
const OnboardingPage = lazy(() => import('../pages/onboarding/OnboardingPage'));
const WelcomePage = lazy(() => import('../pages/onboarding/WelcomePage'));

// Legal pages (lazy load)
const TermsOfServicePage = lazy(() => import('../pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('../pages/PrivacyPolicyPage'));

// Error pages (lazy load)
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

// Loading fallback component (リデザイン v2: antd Spin を排除した素のスピナー)
const LoadingFallback: React.FC = () => (
  <div
    role="status"
    aria-label="読み込み中"
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px',
    }}
  >
    <span
      style={{
        display: 'inline-block',
        width: '36px',
        height: '36px',
        border: '3px solid #e5e7eb',
        borderTopColor: '#4f46e5',
        borderRadius: '9999px',
        animation: 'spin 0.8s linear infinite',
      }}
    />
    <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

// Protected Route wrapper (Outlet pattern)
const ProtectedRoute: React.FC = () => {
  const user = useUser();

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  return <Outlet />;
};

// Public Route wrapper (redirect to dashboard if authenticated)
const PublicRoute: React.FC = () => {
  const user = useUser();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Public routes (Authentication) */}
        <Route element={<PublicRoute />}>
          {/* OAuth Callback - AuthLayout外（独自の全画面ローディングUI） */}
          <Route
            path="/auth/callback"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <AuthCallbackPage />
              </Suspense>
            }
          />
          {/* v2 認証ページは自前で 2 カラムレイアウト (AuthShell) を持つため AuthLayout 不要 */}
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/*" element={<Navigate to="/auth/login" replace />} />
        </Route>

        {/* Protected routes (Dashboard) */}
        <Route element={<ProtectedRoute />}>
          {/* ReonboardingGate: 再オンボーディング判定 (Task C4 - OnboardingGuard を拡張) */}
          <Route element={<ReonboardingGate><Outlet /></ReonboardingGate>}>
          <Route element={<AppLayout />}>
            {/* Dashboard & Overview — リデザイン v2 ホーム「今日」 */}
            <Route
              path="/dashboard"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <TodayPage />
                </Suspense>
              }
            />
            {/* Workspace-specific dashboard (Group A: wsId パラメータ対応) */}
            <Route
              path="/dashboard/:wsId"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <TodayPage />
                </Suspense>
              }
            />

            {/* Project Management */}
            <Route
              path="/projects"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ProjectsPage />
                </Suspense>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ProjectDetailPage />
                </Suspense>
              }
            />

            {/* Usage & Analytics */}
            <Route
              path="/usage"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <UsagePage />
                </Suspense>
              }
            />

            {/* Realtime Meeting Support */}
            <Route
              path="/realtime"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RealtimePage />
                </Suspense>
              }
            />
            <Route
              path="/realtime/setup"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <MeetingSetupPage />
                </Suspense>
              }
            />
            <Route
              path="/realtime/history"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <SessionHistoryPage />
                </Suspense>
              }
            />
            <Route
              path="/realtime/sessions/:sessionId"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <SessionDetailPage />
                </Suspense>
              }
            />

            {/* Preparation (事前準備) */}
            <Route
              path="/preparation"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PreparationPage />
                </Suspense>
              }
            />

            {/* Meeting Import (取り込み。VITE_MEETING_IMPORT_ENABLED=true のみ) */}
            {isMeetingImportEnabled() ? (
              <Route
                path="/import"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ImportMeetingPage />
                  </Suspense>
                }
              />
            ) : null}

            {/* Contacts & Companies */}
            <Route
              path="/contacts"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ContactsPage />
                </Suspense>
              }
            />
            <Route
              path="/contacts/:contactId"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ContactDetailPage />
                </Suspense>
              }
            />
            <Route
              path="/companies"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CompaniesPage />
                </Suspense>
              }
            />
            <Route
              path="/companies/:companyId"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CompanyDetailPage />
                </Suspense>
              }
            />

            {/* User Portal */}
            <Route
              path="/account"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AccountPage />
                </Suspense>
              }
            />
            <Route
              path="/billing"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <BillingPage />
                </Suspense>
              }
            />
            <Route
              path="/api-keys"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <APIKeysPage />
                </Suspense>
              }
            />
            <Route
              path="/integrations"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <IntegrationsPage />
                </Suspense>
              }
            />
            <Route
              path="/integrations/overview"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <IntegrationsPage />
                </Suspense>
              }
            />
            <Route
              path="/integrations/crm"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CRMIntegrationPage />
                </Suspense>
              }
            />

            {/* Workspace Settings */}
            <Route
              path="/settings/workspaces"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <WorkspacesSettingsPage />
                </Suspense>
              }
            />

            {/* Onboarding Settings (Task C5) */}
            <Route
              path="/settings/onboarding"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <OnboardingSettingsPage />
                </Suspense>
              }
            />

            {/* Onboarding */}
            <Route
              path="/onboarding"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <OnboardingPage />
                </Suspense>
              }
            />
            <Route
              path="/welcome"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <WelcomePage />
                </Suspense>
              }
            />
          </Route>
          </Route> {/* /OnboardingGuard */}
        </Route>

        {/* ランディングページ（リデザイン v2、認証不要・自前ナビ/フッター） */}
        <Route
          path="/"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <LandingPage />
            </Suspense>
          }
        />
        {/* デモ（認証不要） */}
        <Route
          path="/demo"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <DemoPage />
            </Suspense>
          }
        />
        {/* サービス説明（認証不要） */}
        <Route
          path="/use-cases"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <UseCasesPage />
            </Suspense>
          }
        />
        {/* 料金ページ（認証不要） */}
        <Route
          path="/pricing"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PricingPage />
            </Suspense>
          }
        />

        {/* Legal pages（認証不要のパブリックルート） */}
        <Route
          path="/terms"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <TermsOfServicePage />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PrivacyPolicyPage />
            </Suspense>
          }
        />

        {/* フォールバック (404) */}
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
